/**
 * Application detail page — /naytive-applications/:kind/:id
 *
 * Opens from the "View" button on the unified NaytiveApplications list.
 * One tabbed page for everything about a single application:
 *
 *   - Overview   — status (edit-in-place), contact, admin notes,
 *                  requested documents, and the user's vault uploads
 *   - Proposal   — (incorporation) the linked setup proposal summary +
 *                  a button to open the full proposal editor. Replaces
 *                  the old standalone PROPOSAL button on the list row.
 *   - Compliances— (incorporation) the linked entity's filing calendar
 *   - Action items — (incorporation) proposal action items
 *   - Call requests — this user's call requests (incl. compliance)
 *   - Transcripts — every logged call attempt + its transcript
 *
 * Powered by /api/naytive/* + /api/calls/*.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import api, { callsAPI, documentTypesAPI, naytiveAPI } from '../services/api';
import { ProposalModal } from './NaytiveProposals';
import './NaytiveApplicationDetail.css';

const VISA_STATUSES = ['pending', 'reviewing', 'scheduled', 'completed', 'cancelled'];
const INCORP_STATUSES = ['pending', 'contacted', 'scheduled', 'completed', 'cancelled'];

const STATUS_PALETTE = {
  pending: { bg: '#FEF3C7', fg: '#92400E' },
  reviewing: { bg: '#DBEAFE', fg: '#1D4ED8' },
  contacted: { bg: '#DBEAFE', fg: '#1D4ED8' },
  scheduled: { bg: '#EDE9FE', fg: '#5B21B6' },
  completed: { bg: '#D1FAE5', fg: '#065F46' },
  cancelled: { bg: '#FEE2E2', fg: '#991B1B' },
};

const NaytiveApplicationDetail = () => {
  const navigate = useNavigate();
  const { kind, id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Active tab. Overview is always present; the rest are added
  // conditionally (proposal/compliances/actions are incorporation-only).
  const [activeTab, setActiveTab] = useState('overview');

  // Inline form drafts.
  const [noteDraft, setNoteDraft] = useState('');
  const [docDraft, setDocDraft] = useState({ documentTypeId: '', notes: '' });

  // Document-type catalog for the "request doc" dropdown.
  const [docTypes, setDocTypes] = useState([]);

  // Incorporation-only: proposal linked to this booking + its action
  // items. `proposal` is the full row (entity_id, status, pricing…),
  // used by the Proposal + Compliances tabs.
  const [proposalId, setProposalId] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [actions, setActions] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [newAction, setNewAction] = useState({ title: '', assigned_to: 'user' });
  // Full proposal editor (opened from the Proposal tab). Reuses the
  // same ProposalModal the queue page uses — kept untouched.
  const [proposalEditorOpen, setProposalEditorOpen] = useState(false);

  // Call attempts + transcripts. Loaded for both kinds (the endpoint is
  // keyed on attorney_request_id, so visa always resolves; incorporation
  // resolves only for the "Call me now" path that mints an attorney
  // request — otherwise it's simply empty).
  const [attempts, setAttempts] = useState(null);
  const [attemptsLoading, setAttemptsLoading] = useState(false);

  // Compliances tab — the linked entity's filing calendar.
  const [filings, setFilings] = useState(null);
  const [filingsLoading, setFilingsLoading] = useState(false);
  const [filingBusy, setFilingBusy] = useState(false);
  // Month drawer opened from a calendar cell: { month, filings }.
  const [filingDrawer, setFilingDrawer] = useState(null);
  // Ad-hoc "Add filing" picker.
  const [addFilingOpen, setAddFilingOpen] = useState(false);
  const [filingTemplates, setFilingTemplates] = useState(null);
  const [newFiling, setNewFiling] = useState({ template_id: '', due_date: '', period_label: '', notes: '' });

  // Per-company vault documents (Documents tab): see everything the company has
  // uploaded, download it, and add a document that lands in the client's vault.
  const [entityDocs, setEntityDocs] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docUploadBusy, setDocUploadBusy] = useState(false);
  const [docNotify, setDocNotify] = useState(true);

  // Call requests tab — this user's compliance call requests.
  const [callRequests, setCallRequests] = useState(null);
  const [callRequestsLoading, setCallRequestsLoading] = useState(false);

  // Email tab — this user's mail thread (inbound + sent), reply/compose.
  const [emails, setEmails] = useState(null);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailBody, setEmailBody] = useState(null);
  const [emailCompose, setEmailCompose] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSyncing, setEmailSyncing] = useState(false);
  // 'this' = only this founder's thread; 'all' = every synced message (incl.
  // mail not attributed to a platform user, e.g. bounce notices, OTP mails).
  const [emailFolder, setEmailFolder] = useState('this');

  const allowedStatuses = useMemo(
    () => (kind === 'incorporation' ? INCORP_STATUSES : VISA_STATUSES),
    [kind],
  );

  // `kind === 'proposal'` = a standalone incorporation proposal that has NO
  // booking/call row (created via "Create a new entity"). There's no calls
  // record to load, so we synthesize a minimal `data.call` from the proposal
  // itself and show only the tabs that make sense (Proposal / Compliances /
  // Action items / Email).

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (kind === 'proposal') {
        const pr = await naytiveAPI.getIncorporationProposal(id);
        const it = pr.data?.item;
        if (!it) throw new Error('Proposal not found');
        setProposal(it);
        setProposalId(Number(id));
        setData({
          call: {
            user_id: it.user_id ?? null,
            user_name: [it.first_name, it.last_name].filter(Boolean).join(' ').trim() || null,
            user_email: it.user_email || null,
            status: it.status,
            jurisdiction_name: it.title || it.jurisdiction_slug || it.country_code,
            phone: null, preferred_date: null, preferred_time: null,
            user_timezone: null, created_at: it.created_at,
          },
          notes: [], requestedDocs: [], userUploads: [],
        });
      } else {
        const res = await callsAPI.get(kind, id);
        setData(res.data?.data);
      }
    } catch (err) {
      console.error('[applicationDetail] load failed', err);
      setError(err.response?.data?.message ?? err.message ?? 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [kind, id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    documentTypesAPI
      .list()
      .then((r) => setDocTypes(r.data?.data?.documentTypes ?? r.data?.data ?? []))
      .catch(() => setDocTypes([]));
  }, []);

  // Call attempts + transcripts — load for both kinds.
  useEffect(() => {
    setAttemptsLoading(true);
    naytiveAPI
      .getAttempts(id)
      .then((r) => setAttempts(r.data?.attempts ?? []))
      .catch(() => setAttempts([]))
      .finally(() => setAttemptsLoading(false));
  }, [id]);

  // Compliance call requests for this user (Call requests tab). Fetched
  // once data resolves so we know the user id.
  useEffect(() => {
    const uid = data?.call?.user_id;
    if (!uid) return;
    setCallRequestsLoading(true);
    naytiveAPI
      .listComplianceCallRequests()
      .then((r) => {
        const items = r.data?.items ?? r.data?.requests ?? [];
        setCallRequests(items.filter((it) => Number(it.user_id) === Number(uid)));
      })
      .catch(() => setCallRequests([]))
      .finally(() => setCallRequestsLoading(false));
  }, [data?.call?.user_id]);

  const loadActions = useCallback(async (pid) => {
    try {
      const r = await naytiveAPI.listProposalActions(pid);
      setActions(r.data?.items || []);
    } catch (err) {
      console.error('[applicationDetail] loadActions failed', err);
    }
  }, []);

  // Incorporation-only: resolve the proposal linked to this booking,
  // then pull its full detail (entity_id powers the Compliances tab)
  // and its action items.
  useEffect(() => {
    if (kind !== 'incorporation') return;
    let cancelled = false;
    (async () => {
      try {
        const r = await naytiveAPI.listIncorporationProposals();
        const match = (r.data?.items || []).find(
          (p) => Number(p.booking_id) === Number(id),
        );
        if (cancelled) return;
        if (match) {
          setProposalId(match.id);
          await loadActions(match.id);
          try {
            const pr = await naytiveAPI.getIncorporationProposal(match.id);
            if (!cancelled) setProposal(pr.data?.item ?? match);
          } catch {
            if (!cancelled) setProposal(match);
          }
        } else {
          setProposalId(null);
          setProposal(null);
          setActions([]);
        }
      } catch (err) {
        console.error('[applicationDetail] proposal lookup failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, id, loadActions]);

  // Standalone proposal — proposalId is the route id; just load its actions.
  useEffect(() => {
    if (kind === 'proposal' && id) loadActions(Number(id));
  }, [kind, id, loadActions]);

  // Compliances — load the entity's filings once we know the entity id.
  const entityId = proposal?.entity_id ?? null;
  const reloadFilings = useCallback(async () => {
    if (!entityId) return [];
    setFilingsLoading(true);
    try {
      const r = await naytiveAPI.listEntityFilings(entityId);
      const items = r.data?.items ?? r.data?.filings ?? [];
      setFilings(items);
      return items;
    } catch {
      setFilings([]);
      return [];
    } finally {
      setFilingsLoading(false);
    }
  }, [entityId]);
  useEffect(() => {
    if (entityId) reloadFilings();
  }, [entityId, reloadFilings]);

  // Documents tab — the company's whole vault, loaded when the tab opens.
  const reloadEntityDocs = useCallback(async () => {
    if (!entityId) return;
    setDocsLoading(true);
    try {
      const r = await naytiveAPI.listEntityDocuments(entityId);
      setEntityDocs(r.data?.items ?? []);
    } catch {
      setEntityDocs([]);
    } finally {
      setDocsLoading(false);
    }
  }, [entityId]);
  useEffect(() => {
    if (activeTab === 'documents' && entityId) reloadEntityDocs();
  }, [activeTab, entityId, reloadEntityDocs]);

  // Open one vault document (view inline, or download). Mirrors openUserDoc but
  // keeps the real filename from the row we already have.
  const openEntityDoc = useCallback(async (doc, disposition) => {
    try {
      const res = await api.get(
        `/naytive/user-documents/${doc.id}/file?disposition=${disposition}`,
        { responseType: 'blob' },
      );
      const objectUrl = URL.createObjectURL(res.data);
      if (disposition === 'attachment') {
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = doc.file_name || `document-${doc.id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } catch (err) {
      alert(err.response?.data?.message ?? 'Could not open the document');
    }
  }, []);

  // Add a document to the client's vault from the admin panel. Reads the file as
  // base64, POSTs it, and (unless the notify box is off) the client is emailed.
  const uploadEntityDoc = useCallback(async () => {
    if (!entityId || docUploadBusy) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,application/pdf';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { alert('File too large (max 10MB).'); return; }
      setDocUploadBusy(true);
      try {
        const fileBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const r = await naytiveAPI.uploadEntityDocument(entityId, {
          fileName: file.name, fileMime: file.type, fileBase64, notify: docNotify,
        });
        await reloadEntityDocs();
        alert(docNotify
          ? (r.data?.emailed ? 'Uploaded. The client has been emailed.' : 'Uploaded. Email could not be sent (check SMTP).')
          : 'Uploaded to the client vault.');
      } catch (err) {
        alert(err.response?.data?.message ?? 'Upload failed');
      } finally {
        setDocUploadBusy(false);
      }
    };
    input.click();
  }, [entityId, docUploadBusy, docNotify, reloadEntityDocs]);

  // Re-run the compliance engine for this entity (adds any missing filings
  // based on the entity's current facts). Same engine the user panel uses.
  const regenerateFilings = async () => {
    if (!entityId || filingBusy) return;
    if (!window.confirm(
      'Re-run the compliance engine for this entity? It adds any filings that '
      + 'are missing from the current facts — existing filings are kept.',
    )) return;
    setFilingBusy(true);
    try {
      const r = await naytiveAPI.regenerateEntityFilings(entityId);
      await reloadFilings();
      alert(`Regenerated — ${r.data?.created ?? 0} filing(s) created.`);
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to regenerate filings');
    } finally {
      setFilingBusy(false);
    }
  };

  // Ad-hoc "Add filing" — pick a template + due date, insert one filing.
  const openAddFiling = async () => {
    setAddFilingOpen((v) => !v);
    if (filingTemplates == null) {
      try {
        const r = await naytiveAPI.listFilingTemplates({});
        setFilingTemplates(r.data?.items ?? r.data?.templates ?? []);
      } catch {
        setFilingTemplates([]);
      }
    }
  };
  const submitAddFiling = async () => {
    const tid = Number(newFiling.template_id);
    if (!entityId || !tid || !newFiling.due_date || filingBusy) return;
    setFilingBusy(true);
    try {
      await naytiveAPI.addEntityFiling(entityId, {
        template_id: tid,
        due_date: newFiling.due_date,
        period_label: newFiling.period_label || undefined,
        notes: newFiling.notes || undefined,
      });
      setNewFiling({ template_id: '', due_date: '', period_label: '', notes: '' });
      setAddFilingOpen(false);
      await reloadFilings();
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to add filing');
    } finally {
      setFilingBusy(false);
    }
  };

  // Mark a filing filed / in progress / waived / re-open. Keeps the open
  // month drawer in sync after the write.
  const setFilingStatus = async (filingId, status) => {
    if (filingBusy) return;
    setFilingBusy(true);
    try {
      await naytiveAPI.updateComplianceFiling(filingId, { status });
      const items = await reloadFilings();
      setFilingDrawer((d) =>
        d ? { ...d, filings: items.filter((f) => filingMonthIdx(f) === d.month) } : d,
      );
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to update filing');
    } finally {
      setFilingBusy(false);
    }
  };

  const addAction = async () => {
    if (!proposalId || !newAction.title.trim() || actionBusy) return;
    setActionBusy(true);
    try {
      await naytiveAPI.addProposalAction(proposalId, {
        title: newAction.title.trim(),
        assigned_to: newAction.assigned_to,
        status: 'todo',
        visible_to_user: true,
        display_order: actions.length + 1,
      });
      setNewAction({ title: '', assigned_to: 'user' });
      await loadActions(proposalId);
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to add action');
    } finally {
      setActionBusy(false);
    }
  };
  const toggleActionDone = async (actionId, done) => {
    if (!proposalId || actionBusy) return;
    setActionBusy(true);
    try {
      await naytiveAPI.updateProposalAction(proposalId, actionId, {
        status: done ? 'done' : 'todo',
      });
      await loadActions(proposalId);
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to update action');
    } finally {
      setActionBusy(false);
    }
  };
  const deleteAction = async (actionId) => {
    if (!proposalId) return;
    if (!window.confirm('Delete this action item? This cannot be undone.')) return;
    setActionBusy(true);
    try {
      await naytiveAPI.deleteProposalAction(proposalId, actionId);
      await loadActions(proposalId);
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to delete action');
    } finally {
      setActionBusy(false);
    }
  };

  const handleStatusChange = async (next) => {
    if (!data?.call || next === data.call.status) return;
    setSaving(true);
    try {
      await callsAPI.updateStatus(kind, id, next);
      await refresh();
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    const body = noteDraft.trim();
    if (!body) return;
    setSaving(true);
    try {
      await callsAPI.addNote(kind, id, body);
      setNoteDraft('');
      await refresh();
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await callsAPI.deleteNote(kind, id, noteId);
      await refresh();
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to delete note');
    }
  };

  const handleRequestDoc = async () => {
    const dtid = Number(docDraft.documentTypeId);
    if (!Number.isFinite(dtid) || dtid <= 0) return;
    setSaving(true);
    try {
      await callsAPI.requestDoc(kind, id, dtid, docDraft.notes || null);
      setDocDraft({ documentTypeId: '', notes: '' });
      await refresh();
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to request doc');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeDoc = async (rdid) => {
    if (!window.confirm('Revoke this document request?')) return;
    try {
      await callsAPI.revokeDocRequest(kind, id, rdid);
      await refresh();
    } catch (err) {
      alert(err.response?.data?.message ?? 'Failed to revoke');
    }
  };

  const openUserDoc = useCallback(async (docId, disposition) => {
    try {
      const res = await api.get(
        `/naytive/user-documents/${docId}/file?disposition=${disposition}`,
        { responseType: 'blob' },
      );
      const blob = res.data;
      const objectUrl = URL.createObjectURL(blob);
      if (disposition === 'attachment') {
        const doc = data?.userUploads?.find((u) => u.id === docId);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = doc?.file_name || `document-${docId}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } catch (err) {
      console.error('[user-document file]', err);
      alert(err.response?.data?.message ?? 'Failed to load file');
    }
  }, [data]);

  // Tab set — order matters. Proposal/Compliances/Action items only
  // apply to incorporation cases (and proposal/actions need a proposal).
  const tabs = useMemo(() => {
    // Standalone proposal: no booking/call, so only proposal-centric tabs.
    if (kind === 'proposal') {
      const t = [{ key: 'proposal', label: 'Proposal' }, { key: 'compliances', label: 'Compliances' }];
      t.push({ key: 'documents', label: 'Documents' });
      if (proposalId) t.push({ key: 'actions', label: 'Action items' });
      t.push({ key: 'email', label: 'Email' });
      return t;
    }
    const t = [{ key: 'overview', label: 'Overview' }];
    if (kind === 'incorporation' && proposalId) t.push({ key: 'proposal', label: 'Proposal' });
    if (kind === 'incorporation') t.push({ key: 'compliances', label: 'Compliances' });
    if (kind === 'incorporation') t.push({ key: 'documents', label: 'Documents' });
    if (kind === 'incorporation' && proposalId) t.push({ key: 'actions', label: 'Action items' });
    t.push({ key: 'calls', label: 'Call requests' });
    t.push({ key: 'email', label: 'Email' });
    t.push({ key: 'transcripts', label: 'Transcripts' });
    return t;
  }, [kind, proposalId]);

  // Email tab — load this user's thread when the tab opens.
  const userId = data?.call?.user_id ?? null;
  const loadEmails = useCallback(async () => {
    setEmailsLoading(true);
    try {
      // 'this' scopes to the founder's own thread; 'all' shows every synced
      // message regardless of who it's attributed to.
      const params = emailFolder === 'all'
        ? { folder: 'all', limit: 200 }
        : { folder: 'all', userId, limit: 100 };
      if (emailFolder !== 'all' && !userId) { setEmails([]); setEmailsLoading(false); return; }
      const r = await naytiveAPI.listEmails(params);
      setEmails(r.data?.items || []);
    } catch {
      setEmails([]);
    } finally {
      setEmailsLoading(false);
    }
  }, [userId, emailFolder]);
  useEffect(() => {
    if (activeTab === 'email') loadEmails();
  }, [activeTab, loadEmails]);

  // Poll the IMAP inbox on demand — pulls any new received mail into
  // inbound_emails, then reloads the list.
  const pollInbox = async () => {
    if (emailSyncing) return;
    setEmailSyncing(true);
    try {
      const r = await naytiveAPI.syncEmails();
      const n = r.data?.synced ?? 0;
      // Switch to "All mail" so freshly-fetched messages are actually visible —
      // most inbound mail (bounces, OTPs) isn't tied to this founder.
      if (n > 0 && emailFolder !== 'all') setEmailFolder('all');
      else await loadEmails();
      if (n > 0) alert(`Fetched ${n} new email${n === 1 ? '' : 's'} — showing all mail.`);
      else alert('Inbox is up to date — no new mail.');
    } catch (err) {
      alert(err.response?.data?.message ?? 'Inbox sync failed. Check IMAP credentials.');
    } finally {
      setEmailSyncing(false);
    }
  };

  const openEmailBody = async (it) => {
    setEmailBody({ loading: true, id: it.id });
    try {
      const r = it.direction === 'in'
        ? await naytiveAPI.getInboundEmail(it.rawId)
        : await naytiveAPI.getSentEmail(it.rawId);
      setEmailBody({ id: it.id, direction: it.direction, from_email: it.from_email, subject: it.subject, ...(r.data?.item || {}) });
    } catch {
      setEmailBody(null);
    }
  };

  // Reply to a received message — pre-fill the composer with the sender, a
  // "Re:" subject, and the quoted original below the cursor.
  const replyToEmail = (it, body) => {
    const to = it.from_email || body?.from_email || '';
    const subj = (it.subject || body?.subject || '').replace(/^\s*re:\s*/i, '');
    const quoted = body?.body_html
      ? `<br><br><blockquote style="margin:0;padding-left:12px;border-left:3px solid #E5E7EB;color:#6B7280">${body.body_html}</blockquote>`
      : body?.body_text
        ? `<br><br><blockquote style="margin:0;padding-left:12px;border-left:3px solid #E5E7EB;color:#6B7280">${escapeHtmlAdmin(body.body_text).replace(/\n/g, '<br>')}</blockquote>`
        : '';
    setEmailCompose({ toEmail: to, subject: `Re: ${subj}`, bodyHtml: `<br>${quoted}` });
  };

  const sendEmailToUser = async () => {
    if (!emailCompose?.subject?.trim()) return;
    setEmailSending(true);
    try {
      // Rich HTML straight from the editor; fall back to the founder's address
      // when replying to nobody in particular.
      const bodyHtml = emailCompose.bodyHtml || '<p>&nbsp;</p>';
      await naytiveAPI.sendEmail({
        toUserId: emailCompose.toEmail ? undefined : userId,
        toEmail: emailCompose.toEmail || data?.call?.user_email || undefined,
        subject: emailCompose.subject.trim(),
        bodyHtml,
      });
      setEmailCompose(null);
      await loadEmails();
      alert('Email sent.');
    } catch (err) {
      alert(err.response?.data?.message ?? 'Send failed');
    } finally {
      setEmailSending(false);
    }
  };

  // Keep the active tab valid if the available set changes (e.g. proposal
  // resolves late, or the user navigates from an incorp to a visa case).
  useEffect(() => {
    if (!tabs.some((t) => t.key === activeTab)) setActiveTab(tabs[0]?.key || 'overview');
  }, [tabs, activeTab]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="nad-page">
          <button
            type="button"
            className="nad-back"
            onClick={() => navigate('/naytive-applications')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to applications
          </button>

          {loading && <div className="nad-empty">Loading…</div>}
          {error && <div className="nad-empty nad-error">{error}</div>}

          {data && !loading && !error && (
            <>
              {/* Header */}
              <header className="nad-header">
                <div className="nad-header-top">
                  <span className={`nad-kind ${kind === 'visa' ? 'is-visa' : 'is-incorp'}`}>
                    {kind === 'visa' ? 'Visa' : 'Entity'}
                  </span>
                  <span className="nad-id">#{id}</span>
                </div>
                <h1 className="nad-title">
                  {data.call.visa_name || data.call.jurisdiction_name || '—'}
                </h1>
                <div className="nad-applicant">
                  <span className="nad-user-name">
                    {data.call.user_name?.trim() || data.call.user_email || '—'}
                  </span>
                  {data.call.user_email && (
                    <span className="nad-user-email">· {data.call.user_email}</span>
                  )}
                </div>
              </header>

              {/* Tab bar */}
              <div style={tabBarStyle} role="tablist">
                {tabs.map((t) => {
                  const active = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      role="tab"
                      aria-selected={active}
                      onClick={() => setActiveTab(t.key)}
                      style={{ ...tabBtnStyle, ...(active ? tabBtnActiveStyle : null) }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {/* ─────────── OVERVIEW ─────────── */}
              {activeTab === 'overview' && (
                <>
                  <div className="nad-grid">
                    <section className="nad-card">
                      <h2>Status</h2>
                      <div className="nad-status-row">
                        {allowedStatuses.map((s) => (
                          <button
                            key={s}
                            disabled={saving}
                            onClick={() => handleStatusChange(s)}
                            className={`nad-status-btn ${data.call.status === s ? 'is-active' : ''}`}
                            style={data.call.status === s ? STATUS_PALETTE[s] : null}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </section>

                    <section className="nad-card">
                      <h2>Contact</h2>
                      <dl className="nad-dl">
                        <dt>Phone</dt>
                        <dd>{data.call.phone ?? '—'}</dd>
                        <dt>Preferred date</dt>
                        <dd>{formatDate(data.call.preferred_date)}</dd>
                        <dt>Preferred time</dt>
                        <dd>{data.call.preferred_time ?? '—'}</dd>
                        <dt>Timezone</dt>
                        <dd>{data.call.user_timezone ?? '—'}</dd>
                        <dt>Created</dt>
                        <dd>{formatDateTime(data.call.created_at)}</dd>
                      </dl>
                    </section>
                  </div>

                  {/* Notes */}
                  <section className="nad-card">
                    <h2>Notes <span className="nad-count">({data.notes.length})</span></h2>
                    <div className="nad-note-list">
                      {data.notes.length === 0 && (
                        <div className="nad-hint">No notes yet. Anything you write here shows up on the user's Notes folder for this application.</div>
                      )}
                      {data.notes.map((n) => (
                        <div key={n.id} className="nad-note">
                          <div className="nad-note-head">
                            <span className="nad-note-author">
                              {n.admin_name?.trim() || n.admin_email || 'admin'}
                            </span>
                            <span className="nad-note-time">{formatDateTime(n.created_at)}</span>
                            <button
                              className="nad-note-del"
                              onClick={() => handleDeleteNote(n.id)}
                              title="Delete"
                            >
                              ×
                            </button>
                          </div>
                          <div className="nad-note-body">{n.body}</div>
                        </div>
                      ))}
                    </div>
                    <textarea
                      className="nad-note-input"
                      placeholder="Write a note that the user can read in their Notes folder…"
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      rows={3}
                    />
                    <div className="nad-actions">
                      <button
                        className="nad-primary"
                        disabled={!noteDraft.trim() || saving}
                        onClick={handleAddNote}
                      >
                        Add note
                      </button>
                    </div>
                  </section>

                  {/* Requested docs */}
                  <section className="nad-card">
                    <h2>Requested documents <span className="nad-count">({data.requestedDocs.length})</span></h2>
                    <div className="nad-doc-list">
                      {data.requestedDocs.length === 0 && (
                        <div className="nad-hint">No documents requested for this application yet.</div>
                      )}
                      {data.requestedDocs.map((d) => {
                        const userUpload = (data.userUploads || []).find(
                          (u) => u.document_type_id === d.document_type_id,
                        );
                        const effectiveStatus = userUpload && d.status === 'pending'
                          ? 'fulfilled'
                          : d.status;
                        return (
                          <div key={d.id} className="nad-doc-row">
                            <div className="nad-doc-name">
                              {d.document_type_name}
                              {d.notes && <span className="nad-doc-note"> — {d.notes}</span>}
                              {userUpload && (
                                <div className="nad-doc-upload-meta">
                                  ✓ {userUpload.file_name || 'file uploaded'}
                                  {userUpload.file_size && (
                                    <span className="nad-doc-size">
                                      {' · '}
                                      {formatBytes(userUpload.file_size)}
                                    </span>
                                  )}
                                  {' · '}
                                  {formatDateTime(userUpload.uploaded_at)}
                                </div>
                              )}
                            </div>
                            <span className="nad-doc-status" data-status={effectiveStatus}>
                              {effectiveStatus}
                            </span>
                            <button
                              className="nad-doc-revoke"
                              onClick={() => handleRevokeDoc(d.id)}
                              title="Revoke"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="nad-doc-form">
                      <select
                        value={docDraft.documentTypeId}
                        onChange={(e) =>
                          setDocDraft((d) => ({ ...d, documentTypeId: e.target.value }))
                        }
                      >
                        <option value="">Select document type…</option>
                        {docTypes.map((dt) => (
                          <option key={dt.id} value={dt.id}>
                            {dt.name} ({dt.code})
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Optional hint (e.g. Last 3 years)"
                        value={docDraft.notes}
                        onChange={(e) =>
                          setDocDraft((d) => ({ ...d, notes: e.target.value }))
                        }
                        maxLength={500}
                      />
                      <button
                        className="nad-primary"
                        disabled={!docDraft.documentTypeId || saving}
                        onClick={handleRequestDoc}
                      >
                        Request
                      </button>
                    </div>
                  </section>

                  {/* User-side uploads */}
                  <section className="nad-card">
                    <h2>
                      User uploads
                      <span className="nad-count">
                        ({(data.userUploads || []).length})
                      </span>
                    </h2>
                    {(!data.userUploads || data.userUploads.length === 0) ? (
                      <div className="nad-hint">
                        The user hasn't uploaded any documents yet.
                      </div>
                    ) : (
                      <div className="nad-upload-list">
                        {data.userUploads.map((u) => (
                          <div key={u.id} className="nad-upload-row">
                            <div className="nad-upload-icon" data-ext={pickExt(u.file_name, u.file_mime_type)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                              </svg>
                            </div>
                            <div className="nad-upload-meta">
                              <div className="nad-upload-name">
                                {u.file_name || u.document_type_name || 'Untitled'}
                              </div>
                              <div className="nad-upload-sub">
                                {u.document_type_name && (
                                  <span className="nad-upload-type">{u.document_type_name}</span>
                                )}
                                {u.file_size > 0 && (
                                  <span> · {formatBytes(u.file_size)}</span>
                                )}
                                <span> · {formatDateTime(u.uploaded_at)}</span>
                              </div>
                            </div>
                            <div className="nad-upload-actions">
                              <button
                                type="button"
                                className="nad-upload-action"
                                title="View file inline"
                                onClick={() => openUserDoc(u.id, 'inline')}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                                View
                              </button>
                              <button
                                type="button"
                                className="nad-upload-action"
                                title="Download file"
                                onClick={() => openUserDoc(u.id, 'attachment')}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="7 10 12 15 17 10" />
                                  <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download
                              </button>
                            </div>
                            <span
                              className="nad-upload-status"
                              data-status={u.verification_status}
                            >
                              {u.verification_status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  {/* Service-agreement consents ("I Agree" on incorporation) */}
                  <section className="nad-card">
                    <h2>
                      Agreements accepted
                      <span className="nad-count">
                        ({(data.userConsents || []).length})
                      </span>
                    </h2>
                    {(!data.userConsents || data.userConsents.length === 0) ? (
                      <div className="nad-hint">
                        No service-agreement acceptance recorded yet.
                      </div>
                    ) : (
                      <div className="nad-upload-list">
                        {data.userConsents.map((c) => (
                          <div key={c.id} className="nad-upload-row">
                            <div className="nad-upload-icon" data-ext="ok">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                <path d="m9 12 2 2 4-4" />
                              </svg>
                            </div>
                            <div className="nad-upload-meta">
                              <div className="nad-upload-name">
                                Service agreement accepted
                                {c.policy_version ? ` · v${c.policy_version}` : ''}
                              </div>
                              <div className="nad-upload-sub">
                                <span>{formatDateTime(c.granted_at)}</span>
                                {c.ip_address && <span> · IP {c.ip_address}</span>}
                              </div>
                            </div>
                            <span
                              className="nad-upload-status"
                              data-status={c.revoked_at ? 'revoked' : 'granted'}
                            >
                              {c.revoked_at ? 'revoked' : 'accepted'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </>
              )}

              {/* ─────────── PROPOSAL ─────────── */}
              {activeTab === 'proposal' && (
                <section className="nad-card">
                  <h2>Setup proposal</h2>
                  {!proposal ? (
                    <div className="nad-hint">No proposal linked to this booking.</div>
                  ) : (
                    <>
                      <dl className="nad-dl">
                        <dt>Title</dt>
                        <dd>{proposal.title || '—'}</dd>
                        <dt>Status</dt>
                        <dd>{proposal.status || '—'}</dd>
                        <dt>Pricing</dt>
                        <dd>{proposal.pricing_kind || '—'}</dd>
                        <dt>Total</dt>
                        <dd>{formatMoney(proposal.total_cost_cents, proposal.currency)}</dd>
                        <dt>Jurisdiction</dt>
                        <dd>{proposal.jurisdiction_slug || proposal.country_code || '—'}</dd>
                        <dt>Entity created</dt>
                        <dd>{proposal.entity_id ? `#${proposal.entity_id}` : 'Not yet'}</dd>
                      </dl>
                      <div className="nad-actions">
                        <button
                          className="nad-primary"
                          onClick={() => setProposalEditorOpen(true)}
                        >
                          Open full proposal
                        </button>
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* ─────────── COMPLIANCES ─────────── */}
              {activeTab === 'compliances' && (
                <section className="nad-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0 }}>
                      Compliance calendar
                      {filings && <span className="nad-count"> ({filings.length})</span>}
                    </h2>
                    {entityId && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="nad-primary"
                          style={{ background: '#fff', color: '#374151', border: '1px solid #D1D5DB' }}
                          disabled={filingBusy}
                          onClick={openAddFiling}
                        >
                          + Add filing
                        </button>
                        <button
                          className="nad-primary"
                          disabled={filingBusy}
                          onClick={regenerateFilings}
                        >
                          {filingBusy ? 'Working…' : 'Regenerate'}
                        </button>
                      </div>
                    )}
                  </div>

                  {addFilingOpen && entityId && (
                    <div style={{ marginTop: 14, padding: 14, border: '1px solid #E5E7EB', borderRadius: 10, background: '#F9FAFB' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 10 }}>
                        <select
                          value={newFiling.template_id}
                          onChange={(e) => setNewFiling((p) => ({ ...p, template_id: e.target.value }))}
                          style={filingInputStyle}
                        >
                          <option value="">
                            {filingTemplates == null ? 'Loading templates…' : 'Select a filing template…'}
                          </option>
                          {(filingTemplates || []).map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.country_code ? ` (${t.country_code})` : ''}
                            </option>
                          ))}
                        </select>
                        <input
                          type="date"
                          value={newFiling.due_date}
                          onChange={(e) => setNewFiling((p) => ({ ...p, due_date: e.target.value }))}
                          style={filingInputStyle}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                        <input
                          placeholder="Period label (optional, e.g. FY2026)"
                          value={newFiling.period_label}
                          onChange={(e) => setNewFiling((p) => ({ ...p, period_label: e.target.value }))}
                          style={filingInputStyle}
                        />
                        <input
                          placeholder="Notes (optional)"
                          value={newFiling.notes}
                          onChange={(e) => setNewFiling((p) => ({ ...p, notes: e.target.value }))}
                          style={filingInputStyle}
                        />
                      </div>
                      <div className="nad-actions" style={{ marginTop: 12 }}>
                        <button
                          className="nad-primary"
                          disabled={filingBusy || !newFiling.template_id || !newFiling.due_date}
                          onClick={submitAddFiling}
                        >
                          Add to calendar
                        </button>
                        <button
                          className="nad-primary"
                          style={{ background: '#6B7280', marginLeft: 8 }}
                          onClick={() => setAddFilingOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {!entityId ? (
                    <div className="nad-hint" style={{ marginTop: 14 }}>
                      No entity yet — the compliance calendar appears once the
                      incorporation is completed and an entity is created.
                    </div>
                  ) : filingsLoading && !filings ? (
                    <div className="nad-hint" style={{ marginTop: 14 }}>Loading filings…</div>
                  ) : !filings || filings.length === 0 ? (
                    <div className="nad-hint" style={{ marginTop: 14 }}>
                      No filings on this entity's calendar yet. Use “Regenerate” to
                      run the compliance engine, or add one manually.
                    </div>
                  ) : (
                    <ComplianceCalendar
                      filings={filings}
                      onOpenMonth={(month, items) => setFilingDrawer({ month, filings: items })}
                    />
                  )}
                </section>
              )}

              {/* ─────────── DOCUMENTS ─────────── */}
              {activeTab === 'documents' && (
                <section className="nad-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0 }}>
                      Documents
                      {entityDocs && <span className="nad-count"> ({entityDocs.length})</span>}
                    </h2>
                    {entityId && (
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6B7280' }}>
                          <input type="checkbox" checked={docNotify} onChange={(e) => setDocNotify(e.target.checked)} />
                          Email the client
                        </label>
                        <button className="nad-primary" disabled={docUploadBusy} onClick={uploadEntityDoc}>
                          {docUploadBusy ? 'Uploading…' : '+ Add document'}
                        </button>
                      </div>
                    )}
                  </div>

                  {!entityId && (
                    <p style={{ color: '#9CA3AF', marginTop: 14 }}>
                      This company has no entity yet. Complete the incorporation to attach and manage documents.
                    </p>
                  )}
                  {entityId && docsLoading && <p style={{ color: '#9CA3AF', marginTop: 14 }}>Loading documents…</p>}
                  {entityId && !docsLoading && entityDocs && entityDocs.length === 0 && (
                    <p style={{ color: '#9CA3AF', marginTop: 14 }}>No documents yet. Add one with the button above.</p>
                  )}
                  {entityId && !docsLoading && entityDocs && entityDocs.length > 0 && (
                    <div style={{ marginTop: 14, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                      {entityDocs.map((doc, i) => (
                        <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderTop: i === 0 ? 'none' : '1px solid #F3F4F6' }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {doc.file_name}
                              {doc.source === 'admin' && (
                                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#2563EB', background: '#EFF6FF', borderRadius: 100, padding: '1px 8px' }}>Team-added</span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                              {doc.doc_type_name || doc.doc_type || 'Document'}
                              {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                              {doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleDateString()}` : ''}
                            </div>
                          </div>
                          <button
                            className="nad-primary"
                            style={{ background: '#fff', color: '#374151', border: '1px solid #D1D5DB' }}
                            onClick={() => openEntityDoc(doc, 'inline')}
                          >
                            View
                          </button>
                          <button className="nad-primary" onClick={() => openEntityDoc(doc, 'attachment')}>
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* ─────────── ACTION ITEMS ─────────── */}
              {activeTab === 'actions' && proposalId && (
                <section className="nad-card">
                  <h2>
                    Action items
                    <span className="nad-count">
                      ({actions.filter((a) => a.visible_to_user === 1).length})
                    </span>
                  </h2>
                  {actions.length === 0 ? (
                    <div className="nad-hint">
                      No action items yet. Add one below — user sees them
                      as pending tasks on the setup-overview card.
                    </div>
                  ) : (
                    <div className="nad-action-list">
                      {actions.map((a) => (
                        <div
                          key={a.id}
                          className="nad-action-row"
                          data-done={a.status === 'done' ? 'true' : 'false'}
                        >
                          <input
                            type="checkbox"
                            checked={a.status === 'done'}
                            disabled={actionBusy}
                            onChange={(e) => toggleActionDone(a.id, e.target.checked)}
                          />
                          <div className="nad-action-body">
                            <div
                              className="nad-action-title"
                              style={{
                                textDecoration: a.status === 'done' ? 'line-through' : 'none',
                                color: a.status === 'done' ? '#9CA3AF' : '#111827',
                              }}
                            >
                              {a.title}
                            </div>
                            <div className="nad-action-meta">
                              <span className="nad-action-assignee" data-role={a.assigned_to}>
                                {a.assigned_to === 'user' ? 'User' : 'Naytive'}
                              </span>
                              {a.visible_to_user === 0 && (
                                <span className="nad-action-hidden">internal</span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="nad-action-delete"
                            disabled={actionBusy}
                            onClick={() => deleteAction(a.id)}
                            title="Delete"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="nad-action-add">
                    <input
                      type="text"
                      placeholder="e.g. Sign Power of Attorney"
                      value={newAction.title}
                      onChange={(e) => setNewAction((p) => ({ ...p, title: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addAction();
                      }}
                    />
                    <select
                      value={newAction.assigned_to}
                      onChange={(e) => setNewAction((p) => ({ ...p, assigned_to: e.target.value }))}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      type="button"
                      className="nad-action-add-btn"
                      disabled={actionBusy || !newAction.title.trim()}
                      onClick={addAction}
                    >
                      Add action
                    </button>
                  </div>
                </section>
              )}

              {/* ─────────── CALL REQUESTS ─────────── */}
              {activeTab === 'calls' && (
                <>
                  <section className="nad-card">
                    <h2>This request</h2>
                    <dl className="nad-dl">
                      <dt>Kind</dt>
                      <dd>{kind === 'visa' ? 'Visa' : 'Incorporation'}</dd>
                      <dt>Phone</dt>
                      <dd>{data.call.phone ?? '—'}</dd>
                      <dt>Preferred</dt>
                      <dd>
                        {formatDate(data.call.preferred_date)}
                        {data.call.preferred_time ? ` · ${data.call.preferred_time}` : ''}
                      </dd>
                      <dt>Status</dt>
                      <dd>{data.call.status ?? '—'}</dd>
                      <dt>Created</dt>
                      <dd>{formatDateTime(data.call.created_at)}</dd>
                    </dl>
                  </section>

                  <section className="nad-card">
                    <h2>
                      Compliance call requests
                      {callRequests && <span className="nad-count"> ({callRequests.length})</span>}
                    </h2>
                    {callRequestsLoading ? (
                      <div className="nad-hint">Loading…</div>
                    ) : !callRequests || callRequests.length === 0 ? (
                      <div className="nad-hint">No compliance call requests from this user.</div>
                    ) : (
                      <div className="nad-filing-list">
                        {callRequests.map((c) => (
                          <div key={c.id} style={filingRowStyle}>
                            <div>
                              <div style={{ fontWeight: 600, color: '#111827' }}>
                                {c.filing_label || 'Compliance call'}
                              </div>
                              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                                {c.phone ? `${c.phone} · ` : ''}
                                {formatDate(c.preferred_date)}
                                {c.preferred_time ? ` · ${c.preferred_time}` : ''}
                              </div>
                            </div>
                            <span className="nad-doc-status" data-status={c.status}>
                              {c.status || 'pending'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </>
              )}

              {/* ─────────── EMAIL ─────────── */}
              {activeTab === 'email' && (
                <section className="nad-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0 }}>
                      Email
                      {emails && <span className="nad-count"> ({emails.length})</span>}
                    </h2>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {/* Scope toggle — fixes "synced but nothing shows" (most mail
                          isn't attributed to this founder). */}
                      <div style={{ display: 'inline-flex', border: '1px solid #D1D5DB', borderRadius: 8, overflow: 'hidden' }}>
                        {[{ k: 'this', l: 'This founder' }, { k: 'all', l: 'All mail' }].map((f) => (
                          <button
                            key={f.k}
                            onClick={() => setEmailFolder(f.k)}
                            style={{
                              padding: '7px 12px', fontSize: 12.5, border: 'none', cursor: 'pointer',
                              background: emailFolder === f.k ? '#2540D9' : '#fff',
                              color: emailFolder === f.k ? '#fff' : '#374151', fontWeight: 600,
                            }}
                          >
                            {f.l}
                          </button>
                        ))}
                      </div>
                      <button
                        className="nad-primary"
                        style={{ background: '#fff', color: '#374151', border: '1px solid #D1D5DB' }}
                        disabled={emailSyncing}
                        onClick={pollInbox}
                        title="Fetch new received mail from the inbox"
                      >
                        {emailSyncing ? 'Polling…' : 'Poll inbox'}
                      </button>
                      <button
                        className="nad-primary"
                        onClick={() => setEmailCompose({ subject: '', bodyHtml: '', toEmail: emailFolder === 'all' ? '' : (data.call.user_email || '') })}
                      >
                        Compose
                      </button>
                    </div>
                  </div>

                  {emailFolder === 'this' && !userId ? (
                    <div className="nad-hint">
                      No platform user linked to this application. Switch to
                      <strong> All mail</strong> to see every synced message.
                    </div>
                  ) : emailsLoading ? (
                    <div className="nad-hint">Loading email…</div>
                  ) : !emails || emails.length === 0 ? (
                    <div className="nad-hint">
                      {emailFolder === 'this'
                        ? <>No emails with {data.call?.user_email || 'this founder'} yet. Most inbound mail isn't tied to a founder — switch to <strong>All mail</strong> or hit <strong>Poll inbox</strong>.</>
                        : 'No mail yet. Hit “Poll inbox” to fetch.'}
                    </div>
                  ) : (
                    <div className="nad-filing-list" style={{ marginTop: 8 }}>
                      {emails.map((it) => (
                        <div key={it.id}>
                          <button
                            onClick={() => openEmailBody(it)}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left',
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: '10px 0', borderTop: '1px solid #F3F4F6',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 999,
                                background: it.direction === 'in' ? '#ECFDF5' : '#EFF6FF',
                                color: it.direction === 'in' ? '#047857' : '#1D4ED8',
                              }}>
                                {it.direction === 'in' ? 'Received' : 'Sent'}
                              </span>
                              <span style={{ fontSize: 13, fontWeight: it.direction === 'in' && !it.is_read ? 600 : 500, color: '#111827', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {it.subject || '(no subject)'}
                              </span>
                              <span style={{ fontSize: 11, color: '#9CA3AF', flex: '0 0 auto' }}>{formatDateTime(it.date)}</span>
                            </div>
                            <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 3 }}>
                              {it.direction === 'in' ? `From ${it.from_email || it.from_name || 'unknown'}` : `To ${it.to_email || 'founder'}`}
                            </div>
                          </button>
                          {emailBody && emailBody.id === it.id && (
                            <div style={{ ...transcriptBoxStyle, maxHeight: 340 }}>
                              {emailBody.loading ? (
                                'Loading…'
                              ) : (
                                <>
                                  {emailBody.body_html ? (
                                    // eslint-disable-next-line react/no-danger
                                    <div dangerouslySetInnerHTML={{ __html: emailBody.body_html }} />
                                  ) : (
                                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                                      {emailBody.body_text || '(empty)'}
                                    </pre>
                                  )}
                                  {it.direction === 'in' && (
                                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #E5E7EB' }}>
                                      <button className="nad-primary" onClick={() => replyToEmail(it, emailBody)}>
                                        ↩ Reply
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {emailCompose && (
                    <div style={{ marginTop: 16, borderTop: '1px solid #E5E7EB', paddingTop: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: '#6B7280' }}>To:</span>
                        <input
                          placeholder="recipient@email.com"
                          value={emailCompose.toEmail || ''}
                          onChange={(e) => setEmailCompose((c) => ({ ...c, toEmail: e.target.value }))}
                          style={{ flex: 1, boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }}
                        />
                      </div>
                      <input
                        placeholder="Subject"
                        value={emailCompose.subject}
                        onChange={(e) => setEmailCompose((c) => ({ ...c, subject: e.target.value }))}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, marginBottom: 8 }}
                      />
                      <RichTextEditor
                        initialHtml={emailCompose.bodyHtml || ''}
                        onChange={(html) => setEmailCompose((c) => ({ ...c, bodyHtml: html }))}
                        placeholder="Write your message…"
                      />
                      <div className="nad-actions" style={{ marginTop: 12 }}>
                        <button className="nad-primary" disabled={emailSending || !emailCompose.subject.trim() || !(emailCompose.toEmail || userId)} onClick={sendEmailToUser}>
                          {emailSending ? 'Sending…' : 'Send email'}
                        </button>
                        <button className="nad-primary" style={{ background: '#6B7280', marginLeft: 8 }} onClick={() => setEmailCompose(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* ─────────── TRANSCRIPTS ─────────── */}
              {activeTab === 'transcripts' && (
                <section className="nad-card">
                  <h2>
                    Call attempts &amp; transcripts
                    {attempts && <span className="nad-count"> ({attempts.length})</span>}
                  </h2>
                  {attemptsLoading && <div className="nad-hint">Loading attempts…</div>}
                  {!attemptsLoading && attempts && attempts.length === 0 && (
                    <div className="nad-hint">
                      No call attempts logged yet
                      {kind === 'incorporation'
                        ? ' (incorporation transcripts appear for calls placed via "Call me now").'
                        : '.'}
                    </div>
                  )}
                  {!attemptsLoading && attempts && attempts.length > 0 && (
                    <div className="nad-attempts">
                      {attempts.map((a) => {
                        const turns = parseTranscript(a.transcript);
                        return (
                          <div key={a.id} className="nad-attempt">
                            <div className="nad-attempt-head">
                              <span className="nad-attempt-num">#{a.attemptNumber}</span>
                              <span className="nad-attempt-outcome" data-outcome={a.outcome}>
                                {a.outcome}
                              </span>
                              <span className="nad-attempt-time">
                                {formatDateTime(a.attemptedAt)}
                              </span>
                              {a.durationSeconds && (
                                <span className="nad-attempt-dur">{a.durationSeconds}s</span>
                              )}
                            </div>
                            {turns.length > 0 ? (
                              <div style={transcriptBoxStyle}>
                                {turns.map((tn, i) => (
                                  <div key={i} style={{ marginBottom: 8 }}>
                                    <span
                                      style={{
                                        ...transcriptRoleStyle,
                                        color: tn.role === 'assistant' ? '#1D4ED8' : '#065F46',
                                      }}
                                    >
                                      {tn.role === 'assistant' ? 'Kim' : 'User'}
                                    </span>
                                    <span style={{ color: '#374151' }}>{tn.text}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="nad-hint" style={{ marginTop: 6 }}>
                                No transcript captured for this attempt.
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </main>

      {/* Full proposal editor — reuses the queue's ProposalModal. */}
      {proposalEditorOpen && proposalId && (
        <ProposalModal
          id={proposalId}
          onClose={() => setProposalEditorOpen(false)}
          onSaved={() => {
            setProposalEditorOpen(false);
            // Refresh the linked proposal summary after an edit.
            naytiveAPI
              .getIncorporationProposal(proposalId)
              .then((pr) => setProposal(pr.data?.item ?? proposal))
              .catch(() => {});
          }}
        />
      )}

      {/* Month drawer — opens from a calendar cell, lists that month's
          filings with full detail + status actions (same data the user sees). */}
      {filingDrawer && (
        <FilingMonthDrawer
          month={filingDrawer.month}
          filings={filingDrawer.filings}
          busy={filingBusy}
          onSetStatus={setFilingStatus}
          onClose={() => setFilingDrawer(null)}
        />
      )}
    </div>
  );
};

// ── Compliance calendar — 12-month grid, mirrors the user panel ──────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// A filing's due month (0-11). Ad-hoc filings without a date sort to Jan.
function filingMonthIdx(f) {
  const d = f.due_date ? new Date(f.due_date) : null;
  return d && !Number.isNaN(d.getTime()) ? d.getMonth() : 0;
}

// Filing status → visual tone. filed/waived = done; past-due & open =
// overdue; within 30 days = due-soon; otherwise upcoming.
function filingTone(f) {
  const st = (f.status || '').toLowerCase();
  if (st === 'filed' || st === 'waived') return 'done';
  const due = f.due_date ? new Date(f.due_date) : null;
  if (!due || Number.isNaN(due.getTime())) return 'upcoming';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due - today) / 86400000);
  if (diffDays < 0) return 'overdue';
  if (diffDays <= 30) return 'due-soon';
  return 'upcoming';
}

const TONE_STYLE = {
  done: { bg: '#D1FAE5', fg: '#065F46', label: 'Filed' },
  overdue: { bg: '#FEE2E2', fg: '#991B1B', label: 'Overdue' },
  'due-soon': { bg: '#FEF3C7', fg: '#92400E', label: 'Due soon' },
  upcoming: { bg: '#EFF6FF', fg: '#1D4ED8', label: 'Upcoming' },
};

// Worst tone across a month's filings drives the cell colour.
function monthTone(items) {
  if (!items.length) return null;
  const tones = items.map(filingTone);
  if (tones.includes('overdue')) return 'overdue';
  if (tones.includes('due-soon')) return 'due-soon';
  if (tones.some((t) => t !== 'done')) return 'upcoming';
  return 'done';
}

const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// "in 5 days" / "3 days ago" / "due today" — mirrors the user panel copy.
function relDays(dueStr) {
  if (!dueStr) return '';
  const due = new Date(dueStr); if (Number.isNaN(due.getTime())) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const d = Math.round((due - today) / 86400000);
  if (d === 0) return 'due today';
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
  return `in ${d} day${d === 1 ? '' : 's'}`;
}

function ComplianceCalendar({ filings, onOpenMonth }) {
  const byMonth = MONTHS.map((_, idx) => filings.filter((f) => filingMonthIdx(f) === idx));
  const next = [...filings]
    .filter((f) => filingTone(f) !== 'done' && f.due_date)
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0];
  return (
    <div style={{ marginTop: 14 }}>
      {next && (
        <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 12 }}>
          Next: <strong style={{ color: '#111827' }}>{next.template_name || next.period_label || `Filing #${next.id}`}</strong>
          {' · due '}{formatDate(next.due_date)}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
        {MONTHS.map((m, idx) => {
          const items = byMonth[idx];
          const has = items.length > 0;
          const tone = monthTone(items);
          const ts = tone ? TONE_STYLE[tone] : null;
          return (
            <button
              key={m}
              type="button"
              disabled={!has}
              onClick={() => has && onOpenMonth(idx, items)}
              style={{
                textAlign: 'left',
                border: `1px solid ${has ? (ts ? ts.bg : '#E5E7EB') : '#F3F4F6'}`,
                background: has ? '#fff' : '#FCFCFB',
                borderRadius: 11,
                padding: '9px 10px',
                minHeight: 76,
                cursor: has ? 'pointer' : 'default',
                opacity: has ? 1 : 0.6,
                display: 'flex',
                flexDirection: 'column',
                gap: 7,
                fontFamily: 'inherit',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: has ? '#111827' : '#9CA3AF' }}>{m}</span>
                {has && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: ts ? ts.fg : '#6B7280', fontVariantNumeric: 'tabular-nums' }}>
                    {String(items.length).padStart(2, '0')}
                  </span>
                )}
              </div>
              {has && (
                <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
                  {items.slice(0, 3).map((f) => {
                    const s = TONE_STYLE[filingTone(f)];
                    return (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                        <span style={{ flex: '0 0 auto', width: 6, height: 6, borderRadius: 999, background: s.fg }} />
                        <span style={{ fontSize: 10.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.template_name || f.period_label || `Filing #${f.id}`}
                        </span>
                      </div>
                    );
                  })}
                  {items.length > 3 && (
                    <span style={{ fontSize: 10, color: '#9CA3AF' }}>+{items.length - 3} more</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Right-side month drawer — mirrors the user panel's CalendarMonthDrawer:
// centred header badge + title, then an accordion of filing cards (date chip,
// title, TL;DR, due + relative days, meta) with status actions.
function FilingMonthDrawer({ month, filings, busy, onSetStatus, onClose }) {
  const STATUS_ACTIONS = [
    { key: 'filed', label: 'Mark filed' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'waived', label: 'Waive' },
    { key: 'upcoming', label: 'Re-open' },
  ];
  const [openId, setOpenId] = useState(filings.length === 1 ? filings[0].id : null);
  const dueSoon = filings.filter((f) => filingTone(f) === 'due-soon').length;
  const allPast = filings.length > 0 && filings.every((f) => filingTone(f) === 'overdue');
  const headBadge = dueSoon > 0
    ? { label: 'Due soon', ...TONE_STYLE['due-soon'] }
    : allPast
      ? { label: 'Past deadline', ...TONE_STYLE.overdue }
      : { label: 'Filings to do', ...TONE_STYLE.upcoming };
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.32)', zIndex: 80 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 430, maxWidth: '100%', background: '#fff',
        zIndex: 90, display: 'flex', flexDirection: 'column', boxShadow: '-12px 0 40px rgba(0,0,0,0.12)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid #F3F4F6', flex: '0 0 auto' }}>
          <button onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: '#6B7280', padding: 0 }}>
            ‹ Back
          </button>
          <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 100, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', color: '#6B7280', fontSize: 17 }}>×</button>
        </div>

        <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          {/* Centred title block */}
          <div style={{ padding: '22px 28px 12px', textAlign: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: headBadge.bg, color: headBadge.fg }}>
              {headBadge.label}
            </span>
            <h2 style={{ fontSize: 20, lineHeight: 1.2, fontWeight: 600, margin: '12px auto 8px', maxWidth: 280, color: '#111827' }}>
              {filings.length} filing{filings.length === 1 ? '' : 's'} in {MONTHS_FULL[month]}
            </h2>
            <p style={{ margin: '0 auto', maxWidth: 300, color: '#6B7280', fontSize: 13, lineHeight: 1.45 }}>
              Tap a filing to see the details and update its status.
            </p>
          </div>

          {/* Filing cards */}
          <div style={{ padding: '4px 24px 28px', display: 'grid', gap: 10 }}>
            {filings.map((f) => {
              const tone = filingTone(f);
              const ts = TONE_STYLE[tone];
              const isOpen = openId === f.id;
              const dd = f.due_date ? new Date(f.due_date) : null;
              const dayNum = dd && !Number.isNaN(dd.getTime()) ? dd.getDate() : '—';
              return (
                <div key={f.id} style={{
                  borderRadius: 12, border: `1px solid ${isOpen ? '#D1D5DB' : '#E5E7EB'}`,
                  background: tone === 'done' ? '#F0FDF4' : tone === 'overdue' ? '#FEFAFA' : '#F9FAFB',
                  overflow: 'hidden',
                }}>
                  <button onClick={() => setOpenId(isOpen ? null : f.id)} style={{
                    display: 'grid', gridTemplateColumns: '48px minmax(0,1fr) 16px', gap: 12, width: '100%',
                    alignItems: 'center', padding: 10, background: 'transparent', border: 'none', cursor: 'pointer',
                    fontFamily: 'inherit', textAlign: 'left',
                  }}>
                    {/* Date chip */}
                    <div style={{ width: 46, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB', background: '#fff' }}>
                      <div style={{ textAlign: 'center', background: ts.fg, color: '#fff', fontSize: 10, fontWeight: 600, padding: '2px 0' }}>
                        {MONTHS[filingMonthIdx(f)].toUpperCase()}
                      </div>
                      <div style={{ textAlign: 'center', color: '#111827', fontSize: 16, fontWeight: 600, padding: '3px 0' }}>
                        {dayNum}
                      </div>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.template_name || f.period_label || `Filing #${f.id}`}
                      </div>
                      {f.template_tldr && (
                        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.template_tldr}</div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 7, color: ts.fg, fontSize: 11.5, flexWrap: 'wrap' }}>
                        <span>{tone === 'overdue' ? 'Was due' : 'Due'} {formatDate(f.due_date)}{relDays(f.due_date) ? ` · ${relDays(f.due_date)}` : ''}</span>
                        <span style={{ fontWeight: 600 }}>{ts.label}</span>
                      </div>
                    </div>
                    <span style={{ color: '#9CA3AF', fontSize: 12, transform: isOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
                  </button>

                  {isOpen && (
                    <div style={{ padding: '2px 14px 14px', display: 'grid', gap: 12, borderTop: '1px solid #F3F4F6' }}>
                      {f.template_tldr && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#9CA3AF' }}>Why this matters</div>
                          <div style={{ fontSize: 12.8, color: '#111827', lineHeight: 1.5, marginTop: 5 }}>{f.template_tldr}</div>
                        </div>
                      )}
                      {(f.category || f.frequency || f.severity || f.period_label) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {[f.period_label, f.category, f.frequency, f.severity].filter(Boolean).map((tag, i) => (
                            <span key={i} style={{ fontSize: 11, color: '#4B5563', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 999, padding: '3px 9px' }}>{tag}</span>
                          ))}
                        </div>
                      )}
                      {f.notes && (
                        <div style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic' }}>{f.notes}</div>
                      )}
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 6 }}>Set status</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {STATUS_ACTIONS.filter((a) => a.key !== (f.status || '').toLowerCase()).map((a) => (
                            <button
                              key={a.key}
                              disabled={busy}
                              onClick={() => onSetStatus(f.id, a.key)}
                              style={{
                                fontSize: 12, padding: '5px 11px', borderRadius: 8,
                                border: '1px solid #D1D5DB', background: '#fff', cursor: busy ? 'default' : 'pointer',
                                color: '#374151', fontFamily: 'inherit',
                              }}
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

function escapeHtmlAdmin(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Lightweight rich-text composer — a contentEditable body with a small
// formatting toolbar (bold/italic/underline, lists, link). Uncontrolled so the
// caret never jumps; the current HTML is pushed up via onChange on every input.
function RichTextEditor({ initialHtml = '', onChange, placeholder = '' }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    // Seed once; never re-write innerHTML from props (that would move the caret).
    if (ref.current && initialHtml && ref.current.innerHTML !== initialHtml) {
      ref.current.innerHTML = initialHtml;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
  };
  const tool = (onDown, label, title) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); onDown(); }}
      style={{ minWidth: 30, height: 28, border: '1px solid #E5E7EB', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#374151', fontFamily: 'inherit' }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ border: '1px solid #D1D5DB', borderRadius: 8, overflow: 'hidden' }}>
      <style>{'.nad-rte:empty:before{content:attr(data-placeholder);color:#9CA3AF}'}</style>
      <div style={{ display: 'flex', gap: 4, padding: '6px 8px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', flexWrap: 'wrap' }}>
        {tool(() => exec('bold'), <b>B</b>, 'Bold')}
        {tool(() => exec('italic'), <i>I</i>, 'Italic')}
        {tool(() => exec('underline'), <u>U</u>, 'Underline')}
        <span style={{ width: 1, background: '#E5E7EB', margin: '2px 3px' }} />
        {tool(() => exec('insertUnorderedList'), '• List', 'Bulleted list')}
        {tool(() => exec('insertOrderedList'), '1. List', 'Numbered list')}
        <span style={{ width: 1, background: '#E5E7EB', margin: '2px 3px' }} />
        {tool(() => { const url = window.prompt('Link URL'); if (url) exec('createLink', url); }, '🔗', 'Insert link')}
      </div>
      <div
        ref={ref}
        className="nad-rte"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        style={{ minHeight: 150, maxHeight: 320, overflowY: 'auto', padding: '10px 12px', fontSize: 13, lineHeight: 1.55, color: '#111827', outline: 'none', fontFamily: 'inherit' }}
      />
    </div>
  );
}

// ── Inline styles (tab chrome + new panels) ──────────────────────────
const filingInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
};
const tabBarStyle = {
  display: 'flex',
  gap: 4,
  flexWrap: 'wrap',
  borderBottom: '1px solid #E5E7EB',
  margin: '18px 0 20px',
};
const tabBtnStyle = {
  padding: '9px 16px',
  border: 'none',
  background: 'none',
  fontSize: 13.5,
  fontWeight: 500,
  color: '#6B7280',
  cursor: 'pointer',
  borderBottom: '2px solid transparent',
  marginBottom: -1,
};
const tabBtnActiveStyle = {
  color: '#111827',
  borderBottom: '2px solid #2540D9',
};
const filingRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '11px 0',
  borderTop: '1px solid #F3F4F6',
};
const transcriptBoxStyle = {
  marginTop: 8,
  padding: 12,
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: 8,
  fontSize: 13,
  lineHeight: 1.5,
  maxHeight: 360,
  overflowY: 'auto',
};
const transcriptRoleStyle = {
  fontWeight: 600,
  marginRight: 6,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

function formatDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function formatDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(1)} KB`;
}

function formatMoney(cents, currency) {
  if (cents == null) return '—';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return `${(n / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })} ${currency || 'USD'}`;
}

/** Normalise the stored transcript (JSON array of {role,text} turns,
 *  or a plain string) into a renderable list of turns. */
function parseTranscript(raw) {
  if (!raw) return [];
  let val = raw;
  if (typeof val === 'string') {
    const s = val.trim();
    if (!s) return [];
    try {
      val = JSON.parse(s);
    } catch {
      return [{ role: 'user', text: s }];
    }
  }
  if (Array.isArray(val)) {
    return val
      .map((t) => ({
        role: t?.role === 'assistant' ? 'assistant' : 'user',
        text: (t?.text ?? t?.content ?? '').toString().trim(),
      }))
      .filter((t) => t.text);
  }
  return [];
}

/** Map filename / mime → short uppercase extension used by the file
 *  icon's coloured tag. */
function pickExt(fileName, mime) {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (ext && ext.length <= 5) return ext;
  if (mime?.includes('pdf')) return 'pdf';
  if (mime?.includes('word')) return 'doc';
  if (mime?.includes('image')) return 'img';
  return 'doc';
}

export default NaytiveApplicationDetail;
